import z from "zod";
import { storedErrorSchema } from "../errors";
import { timestampsSchema } from "../helpers";
import { labelSchema } from "../label";
import { ProjectTypeEnum } from "../projects";
import { TaskFileTypeEnum } from "../task";
import { collectCustomHyperparamIssues } from "./hyperparam-suggestion/registry";
import { ModelBackendEnum } from "./model.enums";
export { ModelBackendEnum };
export var ModelStatusEnum;
(function (ModelStatusEnum) {
    ModelStatusEnum["DRAFT"] = "DRAFT";
    ModelStatusEnum["TRAINING"] = "TRAINING";
    ModelStatusEnum["CONVERTING"] = "CONVERTING";
    ModelStatusEnum["DONE"] = "DONE";
    ModelStatusEnum["ERROR"] = "ERROR";
    ModelStatusEnum["CANCELLED"] = "CANCELLED";
})(ModelStatusEnum || (ModelStatusEnum = {}));
export var ModelOutputTypeEnum;
(function (ModelOutputTypeEnum) {
    ModelOutputTypeEnum["RAW"] = "RAW";
    ModelOutputTypeEnum["RVC4"] = "RVC4";
    ModelOutputTypeEnum["RVC3"] = "RVC3";
    ModelOutputTypeEnum["RVC2"] = "RVC2";
})(ModelOutputTypeEnum || (ModelOutputTypeEnum = {}));
export const isOnnxInferableBackend = (backend) => backend === ModelBackendEnum.ULTRALYTICS ||
    backend === ModelBackendEnum.RF_DETR;
export var ModelRegionEnum;
(function (ModelRegionEnum) {
    ModelRegionEnum["EUROPE_WEST4"] = "europe-west4";
    ModelRegionEnum["US_CENTRAL1"] = "us-central1";
})(ModelRegionEnum || (ModelRegionEnum = {}));
export var ModelQuantizationEnum;
(function (ModelQuantizationEnum) {
    ModelQuantizationEnum["FP16"] = "FP16";
    ModelQuantizationEnum["INT8"] = "INT8";
})(ModelQuantizationEnum || (ModelQuantizationEnum = {}));
export var ModelAugmentationTypeEnum;
(function (ModelAugmentationTypeEnum) {
    ModelAugmentationTypeEnum["FLIP"] = "FLIP";
    ModelAugmentationTypeEnum["ROTATE90"] = "ROTATE90";
    ModelAugmentationTypeEnum["CROP"] = "CROP";
    ModelAugmentationTypeEnum["ROTATION"] = "ROTATION";
    ModelAugmentationTypeEnum["SHEAR"] = "SHEAR";
    ModelAugmentationTypeEnum["GRAYSCALE"] = "GRAYSCALE";
    ModelAugmentationTypeEnum["HUE"] = "HUE";
    ModelAugmentationTypeEnum["SATURATION"] = "SATURATION";
    ModelAugmentationTypeEnum["BRIGHTNESS"] = "BRIGHTNESS";
    ModelAugmentationTypeEnum["CONTRAST"] = "CONTRAST";
    ModelAugmentationTypeEnum["BLUR"] = "BLUR";
    ModelAugmentationTypeEnum["NOISE"] = "NOISE";
    ModelAugmentationTypeEnum["CUTOUT"] = "CUTOUT";
    ModelAugmentationTypeEnum["MOSAIC"] = "MOSAIC";
    ModelAugmentationTypeEnum["CLAHE"] = "CLAHE";
    ModelAugmentationTypeEnum["SHARPEN"] = "SHARPEN";
    ModelAugmentationTypeEnum["MOTION_BLUR"] = "MOTION_BLUR";
    ModelAugmentationTypeEnum["MEDIAN_BLUR"] = "MEDIAN_BLUR";
    ModelAugmentationTypeEnum["DOWNSCALE"] = "DOWNSCALE";
    ModelAugmentationTypeEnum["IMAGE_COMPRESSION"] = "IMAGE_COMPRESSION";
    ModelAugmentationTypeEnum["PERSPECTIVE"] = "PERSPECTIVE";
    ModelAugmentationTypeEnum["EQUALIZE"] = "EQUALIZE";
    ModelAugmentationTypeEnum["POSTERIZE"] = "POSTERIZE";
    ModelAugmentationTypeEnum["RGB_SHIFT"] = "RGB_SHIFT";
})(ModelAugmentationTypeEnum || (ModelAugmentationTypeEnum = {}));
export const modelAugmentationSchema = z.object({
    id: z.number(),
    modelId: z.number(),
    type: z.enum(ModelAugmentationTypeEnum),
    params: z.record(z.string(), z.unknown()),
});
export const modelPreprocessingSchema = z.object({
    id: z.number(),
    modelId: z.number(),
    type: z.enum(ModelAugmentationTypeEnum),
    params: z.record(z.string(), z.unknown()),
    keepOriginal: z.boolean(),
});
export const modelOutputSchema = z.object({
    id: z.number(),
    type: z.enum(ModelOutputTypeEnum),
    filePath: z.string(),
    fileType: z.enum(TaskFileTypeEnum),
    conversionId: z.number().nullable(),
    active: z.boolean(),
    hasArchiveConfig: z.boolean(),
});
export var SuggestionWarningSeverityEnum;
(function (SuggestionWarningSeverityEnum) {
    SuggestionWarningSeverityEnum["INFO"] = "info";
    SuggestionWarningSeverityEnum["WARNING"] = "warning";
    SuggestionWarningSeverityEnum["CRITICAL"] = "critical";
})(SuggestionWarningSeverityEnum || (SuggestionWarningSeverityEnum = {}));
export var SuggestionWarningCodeEnum;
(function (SuggestionWarningCodeEnum) {
    SuggestionWarningCodeEnum["LABEL_SELECTION_FALLBACK"] = "LABEL_SELECTION_FALLBACK";
    SuggestionWarningCodeEnum["LABELS_EXCLUDED"] = "LABELS_EXCLUDED";
})(SuggestionWarningCodeEnum || (SuggestionWarningCodeEnum = {}));
export const suggestionWarningSchema = z.object({
    severity: z.enum(SuggestionWarningSeverityEnum),
    message: z.string(),
    affectedLabels: z.string().array().optional(),
    code: z.enum(SuggestionWarningCodeEnum).optional(),
    params: z.record(z.string(), z.string()).optional(),
});
export const modelAiSuggestionSchema = z.object({
    warnings: suggestionWarningSchema.array(),
    summary: z.string(),
});
export const modelSchema = z.object({
    id: z.number(),
    name: z.string(),
    status: z.enum(ModelStatusEnum),
    epochs: z.number(),
    labels: labelSchema.array(),
    outputs: modelOutputSchema.array(),
    taskIds: z.number().array(),
    datasetVersionId: z.number().nullable(),
    outputTypes: z.enum(ModelOutputTypeEnum).array(),
    backend: z.enum(ModelBackendEnum),
    region: z.enum(ModelRegionEnum),
    quantization: z.enum(ModelQuantizationEnum),
    trainingType: z.enum(ProjectTypeEnum),
    annotationsUsed: z.enum(ProjectTypeEnum).array(),
    splitTrain: z.number(),
    splitValidate: z.number(),
    splitTest: z.number(),
    customHyperparams: z.record(z.string(), z.unknown()),
    aiSuggestion: modelAiSuggestionSchema.nullable(),
    augmentations: modelAugmentationSchema.pick({ type: true, params: true }).array(),
    preprocessings: modelPreprocessingSchema.pick({ type: true, params: true, keepOriginal: true }).array(),
    useGroups: z.boolean(),
    hasCalibration: z.boolean(),
    ...storedErrorSchema.shape,
    finalAccuracy: z.number().nullable(),
    finalLoss: z.number().nullable(),
    bestMap50: z.number().nullable(),
    ...timestampsSchema,
});
export const modelLogMetricsSchema = z
    .object({
    accuracy: z.number(),
    loss: z.number(),
})
    .loose();
export const modelLogPerClassMetricsSchema = z.record(z.string(), z.record(z.string(), z.number()));
export const modelLogConfusionMatrixEntrySchema = z.object({
    labels: z.array(z.string().nullable()),
    matrix: z.array(z.array(z.number())),
});
export const modelLogConfusionMatrixSchema = z.record(z.string(), modelLogConfusionMatrixEntrySchema);
export const modelLogSchema = z.object({
    id: z.number(),
    epoch: z.number(),
    metrics: modelLogMetricsSchema,
    perClassMetrics: modelLogPerClassMetricsSchema.nullable(),
    confusionMatrix: modelLogConfusionMatrixSchema.nullable(),
    createdAt: z.iso.datetime(),
});
export const createModelSchema = modelSchema
    .pick({
    name: true,
    epochs: true,
    outputTypes: true,
    backend: true,
    region: true,
    quantization: true,
    trainingType: true,
    annotationsUsed: true,
    splitTrain: true,
    splitValidate: true,
    splitTest: true,
})
    .extend({
    backend: z
        .literal(ModelBackendEnum.ULTRALYTICS)
        .default(ModelBackendEnum.ULTRALYTICS),
    region: z.enum(ModelRegionEnum).default(ModelRegionEnum.EUROPE_WEST4),
    quantization: z
        .enum(ModelQuantizationEnum)
        .default(ModelQuantizationEnum.FP16),
    labelIds: z.number().array(),
    taskIds: z.number().array().default([]),
    sourceDatasetVersionId: z.number().optional(),
    augmentations: z
        .object({
        type: z.enum(ModelAugmentationTypeEnum),
        params: z.record(z.string(), z.unknown()),
    })
        .array()
        .default([]),
    preprocessings: z
        .object({
        type: z.enum(ModelAugmentationTypeEnum),
        params: z.record(z.string(), z.unknown()),
        keepOriginal: z.boolean().default(false),
    })
        .array()
        .default([]),
    customHyperparams: z.record(z.string(), z.unknown()).default({}),
    aiSuggestion: modelAiSuggestionSchema.nullable().default(null),
    train: z.boolean().default(false),
    useGroups: z.boolean().default(false),
})
    .refine((data) => {
    switch (data.trainingType) {
        case ProjectTypeEnum.CLASSIFICATION:
        case ProjectTypeEnum.SEGMENTATION:
            return (data.annotationsUsed.length === 1 &&
                data.annotationsUsed[0] === data.trainingType);
        case ProjectTypeEnum.DETECTION:
            return (data.annotationsUsed.length > 0 &&
                data.annotationsUsed.every((t) => t === ProjectTypeEnum.DETECTION || t === ProjectTypeEnum.SEGMENTATION));
    }
}, { message: "annotationsUsed is invalid for the selected trainingType" })
    .refine((data) => !data.useGroups || data.trainingType === ProjectTypeEnum.DETECTION, { message: "useGroups is only supported for detection models" })
    .superRefine((data, ctx) => {
    for (const issue of collectCustomHyperparamIssues(data.backend, data.customHyperparams)) {
        ctx.addIssue({
            code: "custom",
            path: ["customHyperparams", issue.key],
            message: issue.message,
        });
    }
});
export const updateModelSchema = createModelSchema;
export const datasetStatsSchema = z.object({
    taskIds: z.number().array().default([]),
    trainingType: z.enum(ProjectTypeEnum),
    annotationsUsed: z.enum(ProjectTypeEnum).array(),
});
export const datasetStatsResponseSchema = z.object({
    labeledCount: z.number(),
    totalCandidateCount: z.number(),
    valid: z.boolean(),
});
export const trainingUsageSchema = z.object({
    used: z.number().int(),
    limit: z.number().int().nullable(),
    periodStart: z.iso.datetime(),
    periodEnd: z.iso.datetime(),
});
//# sourceMappingURL=model.schema.js.map